import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Map as MapIcon, DollarSign, Briefcase, Filter, X, Layers, Target } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useDivision } from '@/context/DivisionContext';
import { TXDOT_DISTRICTS, getDistrictByCounty } from '@/utils/txdotDistricts';
import texasCountiesGeoJSON from '@/data/texas-counties.json';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color scale for heatmap
const getColor = (value, max, colorMode) => {
  if (!value || value === 0) return '#1e293b';
  
  const intensity = Math.min(value / max, 1);
  
  if (colorMode === 'revenue') {
    const r = Math.round(16 + (16 - 16) * (1 - intensity));
    const g = Math.round(185 - 100 * intensity);
    const b = Math.round(129 - 80 * intensity);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (colorMode === 'winrate') {
    // Win rate color scale: Red (low) -> Yellow (mid) -> Green (high)
    if (intensity < 0.33) {
      // Red to Yellow
      const localIntensity = intensity / 0.33;
      return `rgb(239, ${Math.round(68 + 120 * localIntensity)}, 68)`;
    } else if (intensity < 0.66) {
      // Yellow to Green
      const localIntensity = (intensity - 0.33) / 0.33;
      return `rgb(${Math.round(234 - 200 * localIntensity)}, ${Math.round(179 + 30 * localIntensity)}, 68)`;
    } else {
      // Green shades
      const localIntensity = (intensity - 0.66) / 0.34;
      return `rgb(${Math.round(34 - 18 * localIntensity)}, ${Math.round(197 - 20 * localIntensity)}, ${Math.round(94 - 26 * localIntensity)})`;
    }
  } else {
    const r = Math.round(249);
    const g = Math.round(115 + 100 * (1 - intensity));
    const b = Math.round(22 + 50 * (1 - intensity));
    return `rgb(${r}, ${g}, ${b})`;
  }
};

// District colors for distinct visualization
const DISTRICT_COLORS = {
  'Amarillo': '#ef4444',
  'Atlanta': '#f97316',
  'Austin': '#eab308',
  'Beaumont': '#84cc16',
  'Brownwood': '#22c55e',
  'Bryan': '#14b8a6',
  'Childress': '#06b6d4',
  'Corpus Christi': '#0ea5e9',
  'Dallas': '#3b82f6',
  'El Paso': '#6366f1',
  'Fort Worth': '#8b5cf6',
  'Houston': '#a855f7',
  'Laredo': '#d946ef',
  'Lubbock': '#ec4899',
  'Lufkin': '#f43f5e',
  'Odessa': '#fb7185',
  'Paris': '#fda4af',
  'Pharr': '#fcd34d',
  'San Angelo': '#a3e635',
  'San Antonio': '#4ade80',
  'Tyler': '#2dd4bf',
  'Waco': '#38bdf8',
  'Wichita Falls': '#818cf8',
  'Yoakum': '#c084fc'
};

// Legend component
const Legend = ({ max, colorMode, viewMode }) => {
  const formatValue = (val) => {
    if (colorMode === 'revenue') {
      return val >= 1000000 ? `$${(val/1000000).toFixed(1)}M` : 
             val >= 1000 ? `$${(val/1000).toFixed(0)}K` : `$${val}`;
    } else if (colorMode === 'winrate') {
      return `${Math.round(val)}%`;
    }
    return val.toString();
  };

  const getGradient = () => {
    if (colorMode === 'revenue') {
      return 'linear-gradient(to right, #10b981, #059669, #047857)';
    } else if (colorMode === 'winrate') {
      return 'linear-gradient(to right, #ef4444, #eab308, #22c55e)';
    } else {
      return 'linear-gradient(to right, #f97316, #ea580c, #c2410c)';
    }
  };

  return (
    <div className="absolute bottom-8 left-8 z-[1000] bg-[#0f172a]/95 p-4 rounded-lg border border-[rgba(255,255,255,0.1)]">
      <p className="text-xs text-[#94a3b8] uppercase tracking-wider mb-2">
        {viewMode === 'districts' ? 'District' : ''} {colorMode === 'revenue' ? 'Revenue' : colorMode === 'winrate' ? 'Win Rate' : 'Projects'}
      </p>
      <div className="flex items-center gap-2">
        <div className="w-24 h-3 rounded" style={{
          background: getGradient()
        }}></div>
      </div>
      <div className="flex justify-between text-xs text-[#94a3b8] mt-1">
        <span>{colorMode === 'winrate' ? '0%' : '0'}</span>
        <span>{formatValue(max)}</span>
      </div>
    </div>
  );
};

// Fit bounds component
const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, bounds]);
  return null;
};

// Create merged district GeoJSON from county features
const createDistrictGeoJSON = (countiesGeoJSON) => {
  const districtFeatures = [];
  
  Object.entries(TXDOT_DISTRICTS).forEach(([districtName, counties]) => {
    // Find all county features for this district
    const districtCountyFeatures = countiesGeoJSON.features.filter(f => 
      counties.includes(f.properties.NAME)
    );
    
    if (districtCountyFeatures.length > 0) {
      // For simplicity, we'll use MultiPolygon to combine all county geometries
      const coordinates = [];
      
      districtCountyFeatures.forEach(feature => {
        if (feature.geometry.type === 'Polygon') {
          coordinates.push(feature.geometry.coordinates);
        } else if (feature.geometry.type === 'MultiPolygon') {
          coordinates.push(...feature.geometry.coordinates);
        }
      });
      
      districtFeatures.push({
        type: 'Feature',
        properties: {
          NAME: districtName,
          DISTRICT: districtName,
          COUNTY_COUNT: counties.length,
          COUNTIES: counties
        },
        geometry: {
          type: 'MultiPolygon',
          coordinates: coordinates
        }
      });
    }
  });
  
  return {
    type: 'FeatureCollection',
    features: districtFeatures
  };
};

const ProjectMap = () => {
  const { getAuthHeaders } = useAuth();
  const { division } = useDivision();
  
  const [projects, setProjects] = useState([]);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [colorMode, setColorMode] = useState('revenue');
  const [viewMode, setViewMode] = useState('counties'); // 'counties' or 'districts'
  const [selectedItem, setSelectedItem] = useState(null); // county name or district name
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);

  // Create district GeoJSON
  const districtGeoJSON = useMemo(() => createDistrictGeoJSON(texasCountiesGeoJSON), []);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        const [projectsRes, bidsRes] = await Promise.all([
          axios.get(`${API}/projects`, { headers: getAuthHeaders(), params: { division } }),
          axios.get(`${API}/bids/history`, { headers: getAuthHeaders(), params: { division } })
        ]);
        if (isMounted) {
          setProjects(projectsRes.data);
          setBids(bidsRes.data);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (isMounted) {
          toast.error('Failed to load map data');
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [division, getAuthHeaders]);

  // Process data by county (including win rate calculation)
  const countyData = useMemo(() => {
    const data = {};
    
    const filterByDate = (item) => {
      if (!dateRange.start && !dateRange.end) return true;
      const itemDate = new Date(item.created_at || item.letting_date);
      if (dateRange.start && itemDate < new Date(dateRange.start)) return false;
      if (dateRange.end && itemDate > new Date(dateRange.end)) return false;
      return true;
    };

    // First, calculate win rates from ALL bids (regardless of status filter)
    const bidStats = {};
    bids.filter(filterByDate).forEach(bid => {
      const county = bid.project_info?.county?.toUpperCase();
      if (county) {
        if (!bidStats[county]) {
          bidStats[county] = { total: 0, won: 0 };
        }
        bidStats[county].total += 1;
        if (bid.status === 'won') {
          bidStats[county].won += 1;
        }
      }
    });

    // Process open bids
    if (statusFilter === 'all' || statusFilter === 'open') {
      bids.filter(b => b.status === 'open' && filterByDate(b)).forEach(bid => {
        const county = bid.project_info?.county?.toUpperCase();
        if (county) {
          if (!data[county]) {
            data[county] = { revenue: 0, projectCount: 0, items: [], winRate: 0, totalBids: 0, wonBids: 0 };
          }
          const amount = bid.calculation_result?.total_extended_price || 0;
          data[county].revenue += amount;
          data[county].projectCount += 1;
          data[county].items.push({
            type: 'bid',
            status: 'open',
            name: `${county} - ${bid.project_info?.highway || 'N/A'}`,
            amount,
            control: bid.project_info?.control_number
          });
        }
      });
    }

    // Process projects
    projects.filter(filterByDate).forEach(project => {
      if (statusFilter !== 'all' && statusFilter !== 'open' && project.status !== statusFilter) return;
      if (statusFilter === 'open') return;
      
      const county = project.county?.toUpperCase();
      if (county) {
        if (!data[county]) {
          data[county] = { revenue: 0, projectCount: 0, items: [], winRate: 0, totalBids: 0, wonBids: 0 };
        }
        data[county].revenue += project.bid_amount || 0;
        data[county].projectCount += 1;
        data[county].items.push({
          type: 'project',
          status: project.status,
          name: project.project_name,
          amount: project.bid_amount,
          control: project.control_number,
          contractor: project.contractor_name
        });
      }
    });

    // Add win rate data to each county
    Object.keys(data).forEach(county => {
      if (bidStats[county]) {
        data[county].totalBids = bidStats[county].total;
        data[county].wonBids = bidStats[county].won;
        data[county].winRate = bidStats[county].total > 0 
          ? Math.round((bidStats[county].won / bidStats[county].total) * 100) 
          : 0;
      }
    });

    // Also add counties that have bids but no projects/open bids in current filter
    Object.keys(bidStats).forEach(county => {
      if (!data[county]) {
        data[county] = { 
          revenue: 0, 
          projectCount: 0, 
          items: [], 
          winRate: bidStats[county].total > 0 
            ? Math.round((bidStats[county].won / bidStats[county].total) * 100) 
            : 0,
          totalBids: bidStats[county].total,
          wonBids: bidStats[county].won
        };
      }
    });

    return data;
  }, [bids, projects, statusFilter, dateRange]);

  // Aggregate data by district (including win rate)
  const districtData = useMemo(() => {
    const data = {};
    
    Object.entries(TXDOT_DISTRICTS).forEach(([districtName, counties]) => {
      data[districtName] = {
        revenue: 0,
        projectCount: 0,
        items: [],
        counties: counties,
        activeCounties: [],
        totalBids: 0,
        wonBids: 0,
        winRate: 0
      };
      
      counties.forEach(county => {
        if (countyData[county]) {
          data[districtName].revenue += countyData[county].revenue;
          data[districtName].projectCount += countyData[county].projectCount;
          data[districtName].totalBids += countyData[county].totalBids || 0;
          data[districtName].wonBids += countyData[county].wonBids || 0;
          data[districtName].items.push(...countyData[county].items.map(item => ({
            ...item,
            county
          })));
          if (countyData[county].projectCount > 0 || countyData[county].totalBids > 0) {
            data[districtName].activeCounties.push(county);
          }
        }
      });
      
      // Calculate district win rate
      data[districtName].winRate = data[districtName].totalBids > 0
        ? Math.round((data[districtName].wonBids / data[districtName].totalBids) * 100)
        : 0;
    });
    
    return data;
  }, [countyData]);

  // Calculate max values
  const maxValues = useMemo(() => {
    const countyRevenues = Object.values(countyData).map(d => d.revenue);
    const countyCounts = Object.values(countyData).map(d => d.projectCount);
    const countyWinRates = Object.values(countyData).map(d => d.winRate || 0);
    const districtRevenues = Object.values(districtData).map(d => d.revenue);
    const districtCounts = Object.values(districtData).map(d => d.projectCount);
    const districtWinRates = Object.values(districtData).map(d => d.winRate || 0);
    
    return {
      county: {
        revenue: Math.max(...countyRevenues, 1),
        projects: Math.max(...countyCounts, 1),
        winrate: 100 // Always use 100% as max for win rate
      },
      district: {
        revenue: Math.max(...districtRevenues, 1),
        projects: Math.max(...districtCounts, 1),
        winrate: 100 // Always use 100% as max for win rate
      }
    };
  }, [countyData, districtData]);

  // Style function for counties
  const getCountyStyle = (feature) => {
    const countyName = feature.properties.NAME;
    const data = countyData[countyName];
    let value, max;
    
    if (colorMode === 'revenue') {
      value = data?.revenue;
      max = maxValues.county.revenue;
    } else if (colorMode === 'winrate') {
      value = data?.winRate;
      max = maxValues.county.winrate;
    } else {
      value = data?.projectCount;
      max = maxValues.county.projects;
    }
    
    return {
      fillColor: getColor(value, max, colorMode),
      weight: selectedItem === countyName ? 3 : 1,
      opacity: 1,
      color: selectedItem === countyName ? '#f97316' : 'rgba(255,255,255,0.2)',
      fillOpacity: 0.8
    };
  };

  // Style function for districts
  const getDistrictStyle = (feature) => {
    const districtName = feature.properties.NAME;
    const data = districtData[districtName];
    let value, max;
    
    if (colorMode === 'revenue') {
      value = data?.revenue;
      max = maxValues.district.revenue;
    } else if (colorMode === 'winrate') {
      value = data?.winRate;
      max = maxValues.district.winrate;
    } else {
      value = data?.projectCount;
      max = maxValues.district.projects;
    }
    
    return {
      fillColor: getColor(value, max, colorMode),
      weight: selectedItem === districtName ? 4 : 2,
      opacity: 1,
      color: selectedItem === districtName ? '#f97316' : DISTRICT_COLORS[districtName] || '#94a3b8',
      fillOpacity: 0.7
    };
  };

  // Event handlers for counties
  const onEachCounty = (feature, layer) => {
    const countyName = feature.properties.NAME;
    const data = countyData[countyName];
    
    layer.on({
      mouseover: (e) => {
        e.target.setStyle({ weight: 2, color: '#f97316', fillOpacity: 0.9 });
      },
      mouseout: (e) => {
        if (selectedItem !== countyName) {
          e.target.setStyle(getCountyStyle(feature));
        }
      },
      click: () => {
        setSelectedItem(selectedItem === countyName ? null : countyName);
      }
    });

    const tooltipContent = data 
      ? `<strong>${countyName}</strong><br/>` +
        `Projects: ${data.projectCount}<br/>` +
        `Revenue: $${data.revenue.toLocaleString()}<br/>` +
        `Win Rate: ${data.winRate}% (${data.wonBids}/${data.totalBids})`
      : `<strong>${countyName}</strong><br/>No activity`;
    layer.bindTooltip(tooltipContent, { sticky: true });
  };

  // Event handlers for districts
  const onEachDistrict = (feature, layer) => {
    const districtName = feature.properties.NAME;
    const data = districtData[districtName];
    
    layer.on({
      mouseover: (e) => {
        e.target.setStyle({ weight: 4, color: '#f97316', fillOpacity: 0.85 });
      },
      mouseout: (e) => {
        if (selectedItem !== districtName) {
          e.target.setStyle(getDistrictStyle(feature));
        }
      },
      click: () => {
        setSelectedItem(selectedItem === districtName ? null : districtName);
      }
    });

    const tooltipContent = `<strong>${districtName} District</strong><br/>` +
      `Counties: ${data.counties.length}<br/>` +
      `Projects: ${data.projectCount}<br/>` +
      `Revenue: $${data.revenue.toLocaleString()}<br/>` +
      `Win Rate: ${data.winRate}% (${data.wonBids}/${data.totalBids})`;
    layer.bindTooltip(tooltipContent, { sticky: true });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const colors = {
      open: 'bg-yellow-500/20 text-yellow-500',
      active: 'bg-[#10b981]/20 text-[#10b981]',
      invoiced: 'bg-[#f97316]/20 text-[#f97316]',
      paid: 'bg-[#94a3b8]/20 text-[#94a3b8]'
    };
    return colors[status] || colors.active;
  };

  const texasBounds = [[25.8, -106.6], [36.5, -93.5]];

  // Get active districts count
  const activeDistricts = Object.values(districtData).filter(d => d.projectCount > 0).length;
  const activeCounties = Object.keys(countyData).length;
  const totalItems = Object.values(countyData).reduce((s, d) => s + d.projectCount, 0);

  if (division !== 'texas') {
    return (
      <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
        <div className="max-w-7xl mx-auto text-center py-20">
          <MapIcon className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Map View</h1>
          <p className="text-[#94a3b8]">Map view is currently available for Texas only.</p>
          <p className="text-[#64748b] text-sm mt-2">Louisiana map coming soon!</p>
        </div>
      </div>
    );
  }

  // Get selected item data
  const selectedData = viewMode === 'districts' 
    ? (selectedItem ? districtData[selectedItem] : null)
    : (selectedItem ? countyData[selectedItem] : null);

  return (
    <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold text-white mb-1 flex items-center gap-3">
              <MapIcon className="w-8 h-8 text-[#f97316]" />
              Texas Project Map
            </h1>
            <p className="text-[#94a3b8] text-sm">
              {viewMode === 'districts' 
                ? `${activeDistricts} districts with activity • ${totalItems} total items`
                : `${activeCounties} counties with activity • ${totalItems} total items`
              }
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* View Mode Toggle (Counties vs Districts) */}
            <div className="flex bg-[#1e293b] rounded-lg p-1">
              <Button
                size="sm"
                variant={viewMode === 'counties' ? 'default' : 'ghost'}
                onClick={() => { setViewMode('counties'); setSelectedItem(null); }}
                className={viewMode === 'counties' ? 'bg-[#6366f1] text-white' : 'text-[#94a3b8]'}
              >
                <Layers className="w-4 h-4 mr-1" />
                Counties
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'districts' ? 'default' : 'ghost'}
                onClick={() => { setViewMode('districts'); setSelectedItem(null); }}
                className={viewMode === 'districts' ? 'bg-[#8b5cf6] text-white' : 'text-[#94a3b8]'}
              >
                <Layers className="w-4 h-4 mr-1" />
                TXDOT Districts
              </Button>
            </div>

            {/* Color Mode Toggle */}
            <div className="flex bg-[#1e293b] rounded-lg p-1">
              <Button
                size="sm"
                variant={colorMode === 'revenue' ? 'default' : 'ghost'}
                onClick={() => setColorMode('revenue')}
                className={colorMode === 'revenue' ? 'bg-[#10b981] text-white' : 'text-[#94a3b8]'}
              >
                <DollarSign className="w-4 h-4 mr-1" />
                Revenue
              </Button>
              <Button
                size="sm"
                variant={colorMode === 'projects' ? 'default' : 'ghost'}
                onClick={() => setColorMode('projects')}
                className={colorMode === 'projects' ? 'bg-[#f97316] text-white' : 'text-[#94a3b8]'}
              >
                <Briefcase className="w-4 h-4 mr-1" />
                Projects
              </Button>
              <Button
                size="sm"
                variant={colorMode === 'winrate' ? 'default' : 'ghost'}
                onClick={() => setColorMode('winrate')}
                className={colorMode === 'winrate' ? 'bg-[#8b5cf6] text-white' : 'text-[#94a3b8]'}
              >
                <Target className="w-4 h-4 mr-1" />
                Win Rate
              </Button>
            </div>
            
            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`border-[#94a3b8] ${showFilters ? 'bg-[#f97316] text-white border-[#f97316]' : 'text-[#94a3b8]'}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="millmetrix-card p-4 mb-6">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-xs text-[#94a3b8] uppercase">Status</Label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="millmetrix-input mt-1 w-40"
                >
                  <option value="all">All</option>
                  <option value="open">Open Bids</option>
                  <option value="active">Active Projects</option>
                  <option value="invoiced">Invoiced (AR)</option>
                  <option value="paid">Paid/Completed</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-[#94a3b8] uppercase">From Date</Label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="millmetrix-input mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-[#94a3b8] uppercase">To Date</Label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="millmetrix-input mt-1"
                />
              </div>
              {(statusFilter !== 'all' || dateRange.start || dateRange.end) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setStatusFilter('all');
                    setDateRange({ start: '', end: '' });
                  }}
                  className="text-[#94a3b8] hover:text-white"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className="lg:col-span-2">
            <Card className="millmetrix-card overflow-hidden" style={{ height: '600px' }}>
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <p className="text-[#94a3b8]">Loading map...</p>
                </div>
              ) : (
                <div className="relative h-full">
                  <MapContainer
                    bounds={texasBounds}
                    style={{ height: '100%', width: '100%', background: '#0f172a' }}
                    zoomControl={true}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                    />
                    
                    {viewMode === 'counties' ? (
                      <GeoJSON
                        key={`counties-${colorMode}-${statusFilter}-${dateRange.start}-${dateRange.end}`}
                        data={texasCountiesGeoJSON}
                        style={getCountyStyle}
                        onEachFeature={onEachCounty}
                      />
                    ) : (
                      <GeoJSON
                        key={`districts-${colorMode}-${statusFilter}-${dateRange.start}-${dateRange.end}`}
                        data={districtGeoJSON}
                        style={getDistrictStyle}
                        onEachFeature={onEachDistrict}
                      />
                    )}
                    
                    <FitBounds bounds={texasBounds} />
                  </MapContainer>
                  <Legend 
                    max={viewMode === 'districts' 
                      ? (colorMode === 'revenue' ? maxValues.district.revenue : 
                         colorMode === 'winrate' ? maxValues.district.winrate : maxValues.district.projects)
                      : (colorMode === 'revenue' ? maxValues.county.revenue : 
                         colorMode === 'winrate' ? maxValues.county.winrate : maxValues.county.projects)
                    } 
                    colorMode={colorMode}
                    viewMode={viewMode}
                  />
                </div>
              )}
            </Card>
          </div>

          {/* Details Panel */}
          <div>
            <Card className="millmetrix-card p-6" style={{ height: '600px', overflowY: 'auto' }}>
              {selectedItem && selectedData ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white">
                      {selectedItem} {viewMode === 'districts' ? 'District' : 'County'}
                    </h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedItem(null)}
                      className="text-[#94a3b8]"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-[#0f172a] p-3 rounded-lg">
                      <p className="text-xs text-[#94a3b8] uppercase">Projects</p>
                      <p className="text-2xl font-bold text-[#f97316]">
                        {selectedData.projectCount}
                      </p>
                    </div>
                    <div className="bg-[#0f172a] p-3 rounded-lg">
                      <p className="text-xs text-[#94a3b8] uppercase">Revenue</p>
                      <p className="text-2xl font-bold text-[#10b981]">
                        {formatCurrency(selectedData.revenue)}
                      </p>
                    </div>
                  </div>

                  {/* Win Rate Stats */}
                  <div className="bg-[#0f172a] p-3 rounded-lg mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-[#94a3b8] uppercase">Win Rate</p>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        selectedData.winRate >= 50 ? 'bg-[#10b981]/20 text-[#10b981]' :
                        selectedData.winRate >= 25 ? 'bg-yellow-500/20 text-yellow-500' :
                        'bg-red-500/20 text-red-500'
                      }`}>
                        {selectedData.totalBids} bids
                      </span>
                    </div>
                    <div className="flex items-end gap-2">
                      <p className={`text-3xl font-bold ${
                        selectedData.winRate >= 50 ? 'text-[#10b981]' :
                        selectedData.winRate >= 25 ? 'text-yellow-500' :
                        'text-red-400'
                      }`}>
                        {selectedData.winRate}%
                      </p>
                      <p className="text-sm text-[#64748b] pb-1">
                        ({selectedData.wonBids} won)
                      </p>
                    </div>
                    {/* Win rate progress bar */}
                    <div className="mt-2 h-2 bg-[#1e293b] rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          selectedData.winRate >= 50 ? 'bg-[#10b981]' :
                          selectedData.winRate >= 25 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${selectedData.winRate}%` }}
                      />
                    </div>
                  </div>

                  {/* District-specific info */}
                  {viewMode === 'districts' && selectedData.counties && (
                    <div className="mb-4 p-3 bg-[#0f172a] rounded-lg">
                      <p className="text-xs text-[#94a3b8] uppercase mb-2">
                        Counties ({selectedData.counties.length})
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {selectedData.counties.map(county => (
                          <span 
                            key={county}
                            className={`text-xs px-2 py-1 rounded ${
                              selectedData.activeCounties?.includes(county)
                                ? 'bg-[#10b981]/20 text-[#10b981]'
                                : 'bg-[#1e293b] text-[#64748b]'
                            }`}
                          >
                            {county}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Project List */}
                  <h3 className="text-sm font-bold text-[#94a3b8] uppercase mb-3">Projects</h3>
                  <div className="space-y-2">
                    {selectedData.items.map((item, idx) => (
                      <div key={idx} className="bg-[#0f172a] p-3 rounded-lg border border-[rgba(255,255,255,0.05)]">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-white text-sm truncate">{item.name}</p>
                            <p className="text-xs text-[#64748b]">{item.control}</p>
                            {item.county && viewMode === 'districts' && (
                              <p className="text-xs text-[#94a3b8]">{item.county} County</p>
                            )}
                            {item.contractor && (
                              <p className="text-xs text-[#94a3b8] mt-1">{item.contractor}</p>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ml-2 ${getStatusBadge(item.status)}`}>
                            {item.status}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-[#10b981] mt-2">
                          {formatCurrency(item.amount)}
                        </p>
                      </div>
                    ))}
                    
                    {selectedData.items.length === 0 && (
                      <p className="text-[#94a3b8] text-center py-4">No projects in this {viewMode === 'districts' ? 'district' : 'county'}</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <MapIcon className="w-12 h-12 text-[#94a3b8] mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Select a {viewMode === 'districts' ? 'District' : 'County'}
                  </h3>
                  <p className="text-[#94a3b8] text-sm">
                    Click on any {viewMode === 'districts' ? 'district' : 'county'} to see project details
                  </p>
                  
                  {viewMode === 'districts' && (
                    <div className="mt-6 w-full">
                      <p className="text-xs text-[#64748b] uppercase mb-3">TXDOT Districts ({Object.keys(TXDOT_DISTRICTS).length})</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {Object.keys(TXDOT_DISTRICTS).sort().map(district => {
                          const data = districtData[district];
                          return (
                            <button
                              key={district}
                              onClick={() => setSelectedItem(district)}
                              className={`p-2 rounded text-left hover:bg-[#1e293b] transition-colors ${
                                data.projectCount > 0 ? 'text-white' : 'text-[#64748b]'
                              }`}
                              style={{ borderLeft: `3px solid ${DISTRICT_COLORS[district]}` }}
                            >
                              <span className="block font-semibold">{district}</span>
                              {data.projectCount > 0 && (
                                <span className="text-[#10b981]">{data.projectCount} projects</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectMap;
